import { FC } from "react";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { makeSxStyles } from "ui";
import { AdminAccountResDto } from "service/api/estabrookSchemas";
import { TEAM_MEMBERS_COLUMNS } from "../constants/team-members-columns";
import OrganizationAccessToggle from "./OrganizationAccessToggle";
import MemberActionsMenu from "./MemberActionsMenu";

export interface TeamMemberRowProps {
  member: AdminAccountResDto;
  isOrganizationActive: boolean;
  onAccessToggleRequest: (member: AdminAccountResDto) => void;
  onResendInviteRequest: (member: AdminAccountResDto) => void;
  onDeleteRequest: (member: AdminAccountResDto) => void;
}

const ellipsisSx = {
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap" as const,
};

const formatLastActive = (value: string | null | undefined): string => {
  return value ? DateTime.fromISO(value).toFormat("dd MMMM yyyy") : "-";
};

const [
  nameColumn,
  emailColumn,
  accessColumn,
  verifiedColumn,
  roleColumn,
  lastActiveColumn,
  actionsColumn,
] = TEAM_MEMBERS_COLUMNS;

const TeamMemberRow: FC<TeamMemberRowProps> = ({
  member,
  isOrganizationActive,
  onAccessToggleRequest,
  onResendInviteRequest,
  onDeleteRequest,
}) => {
  const styles = useStyles();

  const fullName = `${member.firstName} ${member.lastName}`;
  const isPending = !member.hasSetPassword;

  return (
    <Box sx={styles.card} data-testid={`member-card-${member.id}`}>
      <Box
        sx={{
          ...styles.cell,
          width: nameColumn.size,
          minWidth: nameColumn.minWidth,
        }}
      >
        <Tooltip title={fullName}>
          <Typography sx={{ ...styles.text, ...ellipsisSx }}>
            {fullName}
          </Typography>
        </Tooltip>
      </Box>
      <Box
        sx={{
          ...styles.cell,
          width: emailColumn.size,
          minWidth: emailColumn.minWidth,
        }}
      >
        <Tooltip title={member.email}>
          <Typography sx={{ ...styles.text, ...ellipsisSx }}>
            {member.email}
          </Typography>
        </Tooltip>
      </Box>
      <Box
        sx={{
          ...styles.cell,
          width: accessColumn.size,
          minWidth: accessColumn.minWidth,
        }}
      >
        <Tooltip
          title={
            isOrganizationActive
              ? ""
              : "Enable organization access to manage individual member access"
          }
        >
          <span>
            <OrganizationAccessToggle
              checked={member.isActive}
              onChange={() => onAccessToggleRequest(member)}
              disabled={!isOrganizationActive}
              data-testid={`member-active-toggle-${member.id}`}
            />
          </span>
        </Tooltip>
      </Box>
      <Box
        sx={{
          ...styles.cell,
          width: verifiedColumn.size,
          minWidth: verifiedColumn.minWidth,
        }}
      >
        <Chip
          label={isPending ? "Pending" : "Verified"}
          sx={isPending ? styles.pendingChip : styles.verifiedChip}
        />
      </Box>
      <Box
        sx={{
          ...styles.cell,
          width: roleColumn.size,
          minWidth: roleColumn.minWidth,
        }}
      >
        <Chip label="Admin" sx={styles.roleChip} />
      </Box>
      <Box
        sx={{
          ...styles.cell,
          width: lastActiveColumn.size,
          minWidth: lastActiveColumn.minWidth,
        }}
      >
        <Typography noWrap sx={styles.text}>
          {isPending ? "-" : formatLastActive(member.lastActiveAt)}
        </Typography>
      </Box>
      <Box
        sx={{
          ...styles.cell,
          width: actionsColumn.size,
          minWidth: actionsColumn.minWidth,
          justifyContent: "flex-end",
        }}
      >
        <MemberActionsMenu
          isPending={isPending}
          onResendInvite={() => onResendInviteRequest(member)}
          onDelete={() => onDeleteRequest(member)}
        />
      </Box>
    </Box>
  );
};

const useStyles = makeSxStyles(({ palette: { grey } }) => ({
  card: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    border: `1px solid ${grey[400]}`,
    borderRadius: "12px",
    "&:hover": { background: grey[600] },
  },
  cell: {
    display: "flex",
    alignItems: "center",
    height: "56px",
    padding: "16px",
    boxSizing: "border-box",
  },
  text: {
    color: grey[100],
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: "0.1px",
  },
  pendingChip: {
    borderRadius: "8px",
    height: "24px",
    padding: "4px 8px",
    backgroundColor: grey[400],
    "& .MuiChip-label": {
      padding: 0,
      color: grey[100],
      fontFamily: "Inter",
      fontWeight: 500,
      fontSize: "12px",
      lineHeight: "16px",
    },
  },
  verifiedChip: {
    borderRadius: "8px",
    height: "24px",
    padding: "4px 8px",
    // Same background as the Role chip.
    backgroundColor: "#005140",
    "& .MuiChip-label": {
      padding: 0,
      color: grey[100],
      fontFamily: "Inter",
      fontWeight: 500,
      fontSize: "12px",
      lineHeight: "16px",
    },
  },
  roleChip: {
    borderRadius: "8px",
    height: "24px",
    padding: "4px 8px",
    backgroundColor: "#005140",
    "& .MuiChip-label": {
      padding: 0,
      color: grey[100],
      fontFamily: "Inter",
      fontWeight: 500,
      fontSize: "12px",
      lineHeight: "16px",
    },
  },
}));

export default TeamMemberRow;
